import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useVehicleImage } from '../hooks/useVehicleImage';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FileText, Download, User, Clock, AlertTriangle, PhoneCall, CheckCircle2, ChevronDown, ChevronUp, Car, Gauge, Calendar, Euro, MapPin, Eye, Send, CheckCircle, Fuel, Settings, Palette, Copy, ExternalLink, Mail, X, FileCheck, Undo2, XCircle, Search, Plus, Trash2, Handshake } from 'lucide-react';
import { 
  OfferEsitoDialog, 
  OfferOrdineDialog, 
  AgentOffersSection,
  AdminOfferControls,
  ALL_ESITI,
  isInClosingPhase
} from '../components/offers';
import ClienteQuoteCalculator from '../components/ClienteQuoteCalculator';
import { ReferenteProposalPanel } from '../components/ReferenteProposalPanel';
import PreferenzeQuoteDialog from '../components/PreferenzeQuoteDialog';
import UsatiVelatiSection from '../components/UsatiVelatiSection';
import { AyvensStatusBadge } from '../components/ayvens/AyvensStatusBadge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { isOffertaAccettata } from '../components/offers/offerUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper: visualizzazione km per offerte Longrun (illimitati)
const KM_ILLIMITATI_THRESHOLD = 999999;
const isLongrunOffer = (offer) => {
  if (!offer) return false;
  const vi = offer.vehicle_info || {};
  if (vi.use_longrun === true) return true;
  if ((vi.tipologia_prodotto || '').toLowerCase() === 'longrun') return true;
  if ((offer.km_annui || 0) >= KM_ILLIMITATI_THRESHOLD) return true;
  return false;
};
const formatKmAnnui = (offer) => {
  if (isLongrunOffer(offer)) return 'Illimitati';
  return (offer.km_annui || 0).toLocaleString('it-IT');
};

// Helper per formattare i badge tipo veicolo
const TipoBadge = ({ tipo }) => {
  const styles = {
    nuovo: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    usato: 'bg-amber-100 text-amber-700 border-amber-300',
    '4vantage': 'bg-violet-100 text-violet-700 border-violet-300'
  };
  const labels = { nuovo: 'NUOVO', usato: 'USATO', '4vantage': '4VANTAGE' };
  return (
    <Badge className={`${styles[tipo] || styles.nuovo} text-[10px] px-1.5 py-0.5 border font-semibold`}>
      {labels[tipo] || 'NUOVO'}
    </Badge>
  );
};

// Thumbnail veicolo con immagine Drive (disabilitato per usato)
// Priorità: linkImmagine (es. offerte promo con link manuale) -> Drive fuzzy match
const VehicleThumbnail = ({ marca, modello, grayscale, isUsato, linkImmagine }) => {
  // Disabilita la fetch Drive se abbiamo già un link diretto o se è un usato
  const skipDrive = Boolean(linkImmagine) || isUsato;
  const { imageUrl: driveUrl } = useVehicleImage(skipDrive ? null : marca, skipDrive ? null : modello);
  const finalUrl = linkImmagine || driveUrl;
  return (
    <div className={`w-32 h-24 sm:w-28 sm:h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center overflow-hidden ${grayscale ? 'grayscale' : ''}`}>
      {finalUrl && !isUsato ? (
        <img src={finalUrl} alt={`${marca} ${modello}`} className="w-full h-full object-contain p-1" loading="lazy" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'block'); }} />
      ) : (
        <Car className="w-10 h-10 text-slate-300" />
      )}
    </div>
  );
};

// Helper per lo stato invio cliente
const StatoInvio = ({ inviata, inviatAt }) => {
  if (inviata) {
    return (
      <div className="flex items-center gap-1 text-emerald-600 text-xs">
        <CheckCircle className="w-3.5 h-3.5" />
        <span>Visibile al cliente</span>
        {inviatAt && (
          <span className="text-slate-400 text-[10px]">
            ({new Date(inviatAt).toLocaleDateString('it-IT')})
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 text-slate-400 text-xs">
      <Eye className="w-3.5 h-3.5" />
      <span>Non ancora inviata</span>
    </div>
  );
};

const OffersPage = () => {
  const navigate = useNavigate();
  const { getAuthHeader, user, impersonatedUser, getImpersonateParams } = useAuth();
  const [offers, setOffers] = useState([]);
  const [archivedOffers, setArchivedOffers] = useState([]);
  const [referenteOffers, setReferenteOffers] = useState([]);
  const [agentOffers, setAgentOffers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);  // Protezione double-click per tutte le azioni
  const [validateDialog, setValidateDialog] = useState({ open: false, offer: null });
  const [esitoDialog, setEsitoDialog] = useState({ open: false, offer: null });
  const [meseCompetenza, setMeseCompetenza] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [dataFineNoleggio, setDataFineNoleggio] = useState('');  // Data Fine Noleggio per ordini
  const [selectedEsito, setSelectedEsito] = useState('');
  const [dataRichiamo, setDataRichiamo] = useState('');
  const [showAgentOffers, setShowAgentOffers] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [expandedOffers, setExpandedOffers] = useState({});  // Per card espandibili
  const [searchTerm, setSearchTerm] = useState('');  // Ricerca per codice, cliente, targa
  
  // Stato per popup "Accetta Offerta"
  const [acceptDialog, setAcceptDialog] = useState({ open: false, offer: null, agentEmail: '', agentName: '' });
  const [acceptingOffer, setAcceptingOffer] = useState(false);

  const isAdmin = ['admin', 'super_admin', 'top_agent'].includes(user?.role);
  const isAgente = user?.role === 'agente';
  const isCliente = ['cliente_smart', 'cliente'].includes(user?.role);

  // Fase 2 Sub-release 2.4b — Preferenze durata/km del cliente
  const [preferenze, setPreferenze] = useState(null);  // {durata, km} o null
  const [showPreferenze, setShowPreferenze] = useState(false);

  useEffect(() => {
    if (!isCliente) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/client/preferenze-quote`, { headers: getAuthHeader() });
        if (cancelled) return;
        const prefs = res.data || {};
        if (prefs.durata && prefs.km) {
          setPreferenze({ durata: prefs.durata, km: prefs.km });
        } else {
          // Primo accesso: apri automaticamente il dialog
          setShowPreferenze(true);
        }
      } catch {
        if (!cancelled) setShowPreferenze(true);
      }
    })();
    return () => { cancelled = true; };
  }, [isCliente, getAuthHeader]);

  // ===== CALCOLO PROVVIGIONI PER IMPERSONIFICAZIONE =====
  // Ricalcola le provvigioni basandosi sulla competenza dell'utente impersonato
  // LOGICA GERARCHICA:
  // - TOP_AGENT / ADMIN: prendono l'intera quota del reparto (NO doppia moltiplicazione)
  // - AGENTE: applica la sua competenza sulla quota del reparto
  const calculateDisplayCommission = (offer) => {
    // Se non c'è impersonificazione, usa i valori originali dell'offerta
    if (!impersonatedUser) {
      return {
        provvigione_pre_sconto: offer.provvigione_pre_sconto || offer.provvigione_agente || 0,
        provvigione_agente: offer.provvigione_agente || 0,
        is_lead_vetrina: offer.is_lead_vetrina || false,
        sconto_vetrina_pct: offer.sconto_vetrina_pct || 0
      };
    }
    
    const provvigioneTotale = offer.provvigione_totale || 0;
    const isLeadVetrina = offer.is_lead_vetrina || offer.contact_info?.origine === 'vetrina_pubblica';
    
    // Applica lo split del reparto
    const repartoCommissionSplit = impersonatedUser.reparto_commission_split || 100;
    const provvigioneReparto = provvigioneTotale * (repartoCommissionSplit / 100);
    
    // TOP_AGENT e ADMIN prendono l'intera quota reparto, AGENTE applica la sua competenza
    const ruoloImpersonato = impersonatedUser.role;
    const isRuoloApicale = ['top_agent', 'admin'].includes(ruoloImpersonato);
    const competenzaImpersonato = impersonatedUser.competenza_provvigioni || 30;
    
    let provvigionePreSconto = isRuoloApicale 
      ? provvigioneReparto 
      : provvigioneReparto * (competenzaImpersonato / 100);
    let provvigioneAgente = provvigionePreSconto;
    
    // Applica sconto vetrina se applicabile
    if (isLeadVetrina) {
      provvigioneAgente = provvigionePreSconto * 0.7; // -30%
    }
    
    return {
      provvigione_pre_sconto: Math.round(provvigionePreSconto),
      provvigione_agente: Math.round(provvigioneAgente),
      is_lead_vetrina: isLeadVetrina,
      sconto_vetrina_pct: isLeadVetrina ? 30 : 0
    };
  };

  // Toggle espansione card
  const toggleExpand = (offerId) => {
    setExpandedOffers(prev => ({
      ...prev,
      [offerId]: !prev[offerId]
    }));
  };

  // ===== WORKFLOW ACCETTA OFFERTA =====
  
  // Apri popup conferma accettazione
  const handleOpenAcceptDialog = (offer) => {
    setAcceptDialog({
      open: true,
      offer,
      agentEmail: offer.agent_email || 'preventivi@machenoleggio.it',
      agentName: offer.agent_name || offer.created_by || 'Consulente'
    });
  };

  // Accetta offerta
  const handleAcceptOffer = async () => {
    if (!acceptDialog.offer) return;
    setAcceptingOffer(true);
    try {
      const response = await axios.post(
        `${API}/offers/${acceptDialog.offer.id}/accept`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Offerta accettata! Invia la documentazione all\'agente.');
      setAcceptDialog({ open: false, offer: null, agentEmail: '', agentName: '' });
      fetchOffers(); // Ricarica per aggiornare lo stato
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'accettazione dell\'offerta');
    } finally {
      setAcceptingOffer(false);
    }
  };

  // Annulla accettazione
  const handleCancelAccept = async (offerId) => {
    try {
      await axios.post(
        `${API}/offers/${offerId}/cancel-accept`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Accettazione annullata');
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'annullamento');
    }
  };

  // Non Interessato - Cliente rifiuta l'offerta
  const handleNotInterested = async (offerId) => {
    if (!window.confirm('Sei sicuro di non essere più interessato a questa offerta? L\'offerta verrà rimossa dal sistema.')) {
      return;
    }
    try {
      await axios.post(
        `${API}/offers/${offerId}/not-interested`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Offerta rifiutata. Verrà rimossa dal sistema tra 2 giorni.');
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel rifiuto dell\'offerta');
    }
  };

  // Handler per la ricerca
  const handleSearch = (value) => {
    setSearchTerm(value);
    if (!isCliente) {
      fetchOffers(value);
    }
  };

  // Fetch offers - Endpoint diverso per clienti
  const fetchOffers = useCallback(async (search = '') => {
    try {
      let response;
      if (isCliente) {
        // Cliente: usa endpoint dedicato che mostra solo offerte dalla vetrina + inviate
        response = await axios.get(`${API}/cliente/offerte`, { headers: getAuthHeader() });
        // Separa offerte attive e archiviate
        const allOffers = response.data.offers || [];
        const archived = response.data.archived_offers || [];
        setOffers(allOffers.filter(o => !o.is_archived));
        setReferenteOffers(response.data.referente_offers || []);
        setArchivedOffers(archived);
      } else {
        // Admin/Agente: endpoint normale
        // Passa view_as se Admin sta impersonando un Capogruppo
        const impersonateParams = getImpersonateParams();
        const queryParams = new URLSearchParams({ status: 'offerta', ...impersonateParams });
        
        // Aggiungi parametro di ricerca se presente
        if (search && search.trim()) {
          queryParams.append('search', search.trim());
        }
        
        response = await axios.get(`${API}/offers?${queryParams}`, { headers: getAuthHeader() });
        const sorted = response.data.sort((a, b) => {
          // Priorità 1: Offerte accettate dal cliente (pending_docs) in cima
          const aAccettato = isOffertaAccettata(a) ? 0 : 1;
          const bAccettato = isOffertaAccettata(b) ? 0 : 1;
          if (aAccettato !== bAccettato) return aAccettato - bAccettato;
          
          // Priorità 2: Esiti importanti
          const priority = { 'contratto_firmato': 1, 'contratto_da_firmare': 2, 'approvato': 3 };
          const aPriority = priority[a.esito] || 99;
          const bPriority = priority[b.esito] || 99;
          if (aPriority !== bPriority) return aPriority - bPriority;
          
          // Priorità 3: Data creazione (più recenti prima)
          return new Date(b.created_at) - new Date(a.created_at);
        });
        setOffers(sorted);
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Errore nel caricamento delle offerte');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, isCliente, getImpersonateParams]);

  // Fetch agent offers (Admin only)
  const fetchAgentOffers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(`${API}/offers/agenti`, { headers: getAuthHeader() });
      setAgentOffers(response.data);
    } catch (error) {
      console.error('Error fetching agent offers:', error);
    }
  }, [getAuthHeader, isAdmin]);

  // Fetch agents (Admin only)
  const fetchAgents = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(`${API}/agents`, { headers: getAuthHeader() });
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  }, [getAuthHeader, isAdmin]);

  useEffect(() => {
    fetchOffers();
    fetchAgents();
    fetchAgentOffers();
  }, [fetchOffers, fetchAgents, fetchAgentOffers]);

  useEffect(() => {
    window.addEventListener('ayvens-questionario-saved', fetchOffers);
    return () => window.removeEventListener('ayvens-questionario-saved', fetchOffers);
  }, [fetchOffers]);

  // Handlers
  // Download PDF e marca automaticamente come inviata al cliente (workflow unificato)
  const handleDownloadPdf = async (offerId, autoSendToClient = true) => {
    try {
      const response = await axios.get(`${API}/offers/${offerId}/pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `offerta_${offerId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // WORKFLOW UNIFICATO: Se non è già inviata e autoSendToClient è true, invia automaticamente
      if (autoSendToClient && !isCliente) {
        const offer = offers.find(o => o.id === offerId);
        if (offer && !offer.inviata_cliente) {
          try {
            await axios.post(`${API}/offers/${offerId}/send-to-client`, {}, { headers: getAuthHeader() });
            toast.success('PDF scaricato e offerta inviata al cliente!');
            fetchOffers();
            return;
          } catch (e) {
            // Se fallisce l'invio, mostra solo il toast del download
            console.log('Auto-send failed:', e);
          }
        }
      }
      
      toast.success('PDF scaricato');
    } catch (error) {
      if (!error.ayvensHandled) toast.error('Errore nel download del PDF');
    }
  };

  // Invia offerta al cliente (rende visibile nell'area riservata) - ora usato solo per invio manuale
  const handleSendToClient = async (offerId) => {
    try {
      // Usa l'endpoint send-email per inviare email con allegati PDF
      const response = await axios.post(`${API}/offers/${offerId}/send-email`, {}, { headers: getAuthHeader() });
      const destinatario = response.data?.to || 'cliente';
      toast.success(`Email inviata correttamente all'indirizzo ${destinatario}`);
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio email');
    }
  };

  // Copia link offerta negli appunti (per Agenti/Admin)
  const handleCopyOfferLink = async (offerId) => {
    const offerUrl = `${window.location.origin}/offerte?id=${offerId}`;
    try {
      await navigator.clipboard.writeText(offerUrl);
      toast.success('Link copiato negli appunti!');
    } catch (error) {
      // Fallback per browser che non supportano clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = offerUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Link copiato negli appunti!');
    }
  };

  const handleOpenValidateDialog = (offer) => {
    const now = new Date();
    const defaultMese = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setMeseCompetenza(defaultMese);
    setSelectedAgentId(offer.user_id);
    
    // Calcola data fine noleggio default (oggi + durata_mesi)
    const durataMesi = offer.durata_mesi || 48;
    const dataFine = new Date(now);
    dataFine.setMonth(dataFine.getMonth() + durataMesi);
    setDataFineNoleggio(dataFine.toISOString().split('T')[0]);
    
    setValidateDialog({ open: true, offer });
  };

  const handleValidate = async () => {
    if (!validateDialog.offer) return;
    const offerId = validateDialog.offer.id;
    
    // Validazione: data_fine_noleggio obbligatoria
    if (!dataFineNoleggio) {
      toast.error('La Data Fine Noleggio è obbligatoria per creare un ordine');
      return;
    }
    
    // Protezione double-click
    if (actionLoading === offerId) return;
    setActionLoading(offerId);
    
    try {
      await axios.post(`${API}/offers/${offerId}/validate`, {
        mese_competenza: meseCompetenza || null,
        agent_id: selectedAgentId !== validateDialog.offer.user_id ? selectedAgentId : null,
        crea_contratto: true,
        data_fine_noleggio: dataFineNoleggio
      }, { headers: getAuthHeader() });
      toast.success('Ordine confermato! Contratto attivato e cliente promosso ad ACTIVE');
      setValidateDialog({ open: false, offer: null });
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione ordine');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendSollecito = async (offerId) => {
    // Protezione double-click
    if (actionLoading === offerId) return;
    setActionLoading(offerId);
    
    try {
      await axios.post(`${API}/offers/${offerId}/sollecito`, {}, { headers: getAuthHeader() });
      toast.success('Richiesta esito inviata all\'agente!');
      fetchOffers();
      fetchAgentOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio del sollecito');
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenEsitoDialog = (offer) => {
    setSelectedEsito(offer.esito || '');
    setDataRichiamo('');
    setEsitoDialog({ open: true, offer });
  };

  const handleSaveEsito = async () => {
    if (!selectedEsito) {
      toast.error('Seleziona un esito');
      return;
    }
    if (selectedEsito === 'richiamare' && !dataRichiamo) {
      toast.error('Inserisci la data di richiamo');
      return;
    }
    
    const offerId = esitoDialog.offer?.id;
    if (!offerId) return;
    
    // Protezione double-click
    if (actionLoading === offerId) return;
    setActionLoading(offerId);
    
    try {
      await axios.put(`${API}/offers/${offerId}/esito`, {
        esito: selectedEsito,
        data_richiamo: selectedEsito === 'richiamare' ? dataRichiamo : null
      }, { headers: getAuthHeader() });
      toast.success('Esito salvato');
      setEsitoDialog({ open: false, offer: null });
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setActionLoading(null);
    }
  };

  // Helper functions
  const formatDataRichiamo = (offer) => {
    if (offer.esito !== 'richiamare' || !offer.data_richiamo) return null;
    try {
      const date = new Date(offer.data_richiamo);
      return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return offer.data_richiamo;
    }
  };

  const getScadenzaBadge = (offer) => {
    // Per stato "richiamare", mostra la data di richiamo invece della scadenza
    if (offer.esito === 'richiamare') {
      const dataRichiamo = formatDataRichiamo(offer);
      return (
        <div className="text-xs">
          <Badge className="bg-amber-100 text-amber-700">
            <PhoneCall className="w-3 h-3 mr-1" />
            Richiamare
          </Badge>
          {dataRichiamo && (
            <p className="text-amber-600 mt-1 font-medium">il {dataRichiamo}</p>
          )}
        </div>
      );
    }
    
    if (!offer.scadenza) return null;
    const scadenza = new Date(offer.scadenza);
    const now = new Date();
    const diffDays = Math.ceil((scadenza - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return <Badge className="bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3 mr-1" />Scaduta</Badge>;
    }
    if (diffDays <= 2) {
      return <Badge className="bg-orange-100 text-orange-700"><Clock className="w-3 h-3 mr-1" />{diffDays}g</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700"><Clock className="w-3 h-3 mr-1" />{diffDays}g</Badge>;
  };

  // Determina se l'Admin può modificare l'esito di questa offerta
  const canAdminEditEsito = (offer) => {
    // Se l'offerta è dell'Admin stesso, può sempre modificarla
    if (offer.user_id === user?.id) return true;
    
    // Per offerte di altri agenti:
    // Admin può intervenire SOLO se:
    // 1. Cliente è Prospect
    // 2. Offerta è in stato "richiesta_documentazione" o "pratica_credito"
    const isProspect = offer.contact_info?.tipo_profilo === 'prospect';
    const isInValutazione = ['richiesta_documentazione', 'pratica_credito'].includes(offer.esito);
    
    return isProspect && isInValutazione;
  };

  const getEsitoBadge = (esito) => {
    const opt = ALL_ESITI.find(o => o.value === esito);
    if (!opt) return (
      <Badge className="bg-amber-400 text-amber-900 text-sm font-bold px-3 py-1 shadow-md animate-pulse">
        ⚠️ DA ESITARE
      </Badge>
    );
    return <Badge className={opt.color}>{opt.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="offers-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            {isCliente ? 'I Miei Preventivi' : 'Le mie Offerte'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isCliente 
              ? 'I tuoi preventivi di noleggio. Puoi salvarne fino a 3 contemporaneamente.'
              : 'Storico delle offerte generate e gestione degli esiti'}
          </p>
        </div>
        {isCliente && (() => {
          // 2.6.11 — Blindatura client-side: escludi anche i venduti e i rifiutati/non_interessato
          // per proteggere il contatore da eventuali regressioni backend.
          const savedCount = (offers || []).filter(o =>
            o.saved_by_client &&
            !o.is_sold &&
            !o.is_sold_vehicle &&
            o.status !== 'contratto' &&
            o.status !== 'rifiutato_cliente' &&
            o.status !== 'rifiutato' &&
            o.esito !== 'non_interessato' &&
            o.esito !== 'rifiutato'
          ).length;
          const slotMax = 3;
          const slotFull = savedCount >= slotMax;
          return (
            <div className="flex items-center gap-2 flex-wrap" data-testid="slot-indicator">
              <div className={`px-3 py-1.5 rounded-md text-sm font-semibold border ${slotFull ? 'bg-amber-50 text-amber-800 border-amber-300' : 'bg-emerald-50 text-emerald-800 border-emerald-300'}`}>
                {savedCount} / {slotMax} preventivi salvati
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/dashboard/catalogo-offerte')}
                className="gap-1.5"
                data-testid="btn-back-to-catalogo"
                disabled={slotFull}
              >
                <Plus className="w-4 h-4" /> Aggiungi dal catalogo
              </Button>
            </div>
          );
        })()}
      </div>

      {/* Barra di Ricerca - Solo per Admin/Agente */}
      {!isCliente && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            type="text"
            placeholder="Cerca per codice offerta, cliente, targa..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10 pr-4 py-2 w-full max-w-md"
          />
        </div>
      )}

      {/* Vista Cliente - Card Espandibili con Player Nextlane */}
      {isCliente ? (
        <div className="space-y-6">
          {/* Offerte Attive del Cliente + blocco separato Proposte del Referente */}
          {[
            { key: 'own', list: offers, title: `I Tuoi Preventivi (${offers.length})`, subtitle: null },
            { key: 'referente', list: referenteOffers, title: `Proposte del tuo Referente (${referenteOffers.length})`, subtitle: 'Offerte preparate per te dal tuo consulente: non occupano i 3 posti dei tuoi preventivi.' },
          ].filter(section => section.key === 'own' || section.list.length > 0).map(section => (
          <Card key={section.key} className={section.key === 'referente' ? 'border-2 border-blue-200 bg-blue-50/30' : ''} data-testid={`cliente-section-${section.key}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {section.key === 'referente'
                  ? <Handshake className="w-5 h-5 text-blue-600" />
                  : <FileText className="w-5 h-5 text-emerald-600" />}
                {section.title}
              </CardTitle>
              {section.subtitle && <p className="text-sm text-slate-500">{section.subtitle}</p>}
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : section.list.length > 0 ? (
                <div className="space-y-4">
                  {section.list.map(offer => {
                    const isExpanded = expandedOffers[offer.id];
                    const tipoVeicolo = offer.vehicle_info?.tipo || 'nuovo';
                    const isUsato = tipoVeicolo === 'usato';
                    const hasPlayer = offer.vehicle_info?.link_immagine;
                    // Veicolo venduto: la card è veicolo-venduto OPPURE l'offerta ha vehicle_id non più in listino
                    const isSold = offer.is_sold || offer.is_sold_vehicle;
                    const isPendingDocs = isOffertaAccettata(offer);
                    // STATO "NON INTERESSATO" - Cliente ha rifiutato
                    const isNonInteressatoCliente = offer.esito === 'non_interessato' || offer.status === 'rifiutato_cliente';
                    
                    return (
                      <div 
                        key={offer.id}
                        className={`rounded-xl border-2 bg-white overflow-hidden transition-all relative ${
                          isNonInteressatoCliente
                            ? 'border-slate-400 bg-slate-100 opacity-70 grayscale pointer-events-none'
                            : isSold 
                              ? 'border-red-300 opacity-90' 
                              : isPendingDocs
                                ? 'border-violet-400 bg-violet-50/30'
                                : 'border-slate-200 hover:border-emerald-300 hover:shadow-md'
                        }`}
                        data-testid={`cliente-offer-${offer.id}`}
                      >
                        {/* TIMBRO NERO "NON INTERESSATO" - Vista Cliente */}
                        {isNonInteressatoCliente && (
                          <div className="absolute inset-0 z-20 pointer-events-none flex items-center justify-center">
                            <div 
                              className="transform -rotate-6 border-4 border-slate-800 rounded-lg px-5 py-2 bg-slate-900/95"
                              style={{
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)'
                              }}
                            >
                              <span 
                                className="text-white font-black text-2xl sm:text-3xl tracking-wider uppercase"
                                style={{
                                  fontFamily: 'Impact, Haettenschweiler, sans-serif'
                                }}
                              >
                                NON INTERESSATO
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Badge "In Attesa Documentazione" per offerte accettate (legacy - solo se non c'è già flusso Visto Verde) */}
                        {isPendingDocs && !isSold && !isNonInteressatoCliente && !offer.visto_verde && !offer.documenti_conferma_at && (
                          <div className="absolute top-3 right-3 z-10">
                            <Badge className="bg-violet-600 text-white text-xs px-2 py-1 flex items-center gap-1">
                              <FileCheck className="w-3 h-3" />
                              In Attesa Documentazione
                            </Badge>
                          </div>
                        )}
                        
                        {/* TIMBRO VENDUTA - Overlay d'impatto */}
                        {isSold && !isNonInteressatoCliente && (
                          <div className="absolute inset-0 z-20 pointer-events-none flex items-center justify-center">
                            <div 
                              className="transform -rotate-12 border-8 border-red-600 rounded-lg px-6 py-2 bg-red-600/10 backdrop-blur-[1px]"
                              style={{
                                boxShadow: '0 0 20px rgba(220, 38, 38, 0.3)'
                              }}
                            >
                              <span 
                                className="text-red-600 font-black text-4xl sm:text-5xl tracking-wider uppercase"
                                style={{
                                  textShadow: '2px 2px 0 white, -2px -2px 0 white, 2px -2px 0 white, -2px 2px 0 white',
                                  fontFamily: 'Impact, Haettenschweiler, sans-serif'
                                }}
                              >
                                VENDUTA
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Header Card - Sempre visibile, cliccabile per espandere */}
                        <div 
                          className={`p-4 ${(isSold || isNonInteressatoCliente) ? 'cursor-default' : 'cursor-pointer'}`}
                          onClick={() => !(isSold || isNonInteressatoCliente) && toggleExpand(offer.id)}
                          data-testid={`expand-offer-${offer.id}`}
                        >
                          <div className="flex flex-col sm:flex-row gap-4">
                            {/* Thumbnail Veicolo */}
                            <div className="relative flex-shrink-0 mx-auto sm:mx-0">
                              <VehicleThumbnail marca={offer.vehicle_info?.marca} modello={offer.vehicle_info?.modello} linkImmagine={offer.vehicle_info?.link_immagine || offer.link_immagine} grayscale={isSold || isNonInteressatoCliente} isUsato={tipoVeicolo === 'usato'} />
                              {/* Badge Tipo Veicolo */}
                              <div className="absolute -top-2 -left-2">
                                <TipoBadge tipo={tipoVeicolo} />
                              </div>
                            </div>
                            
                            {/* Info Principali */}
                            <div className="flex-1 min-w-0 text-center sm:text-left">
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                <div>
                                  {/* Codice Offerta Univoco - BEN VISIBILE */}
                                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                    {offer.codice_offerta && (
                                      <span className="inline-block text-xs font-mono text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                                        {offer.codice_offerta}
                                      </span>
                                    )}
                                    {/* VehicleID / Promo ID Label */}
                                    {offer.promo_id_label ? (
                                      <span className="inline-block text-xs font-mono text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                                        {offer.promo_id_label}
                                      </span>
                                    ) : (offer.vehicle_info?.vehicle_id || offer.vehicle_id) ? (
                                      <span className="inline-block text-xs font-mono text-slate-600 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                                        ID: {offer.vehicle_info?.vehicle_id || offer.vehicle_id}
                                      </span>
                                    ) : null}
                                    {/* Badge MANUALE per offerte Clona e Sovrascrivi - ACCESO */}
                                    {offer.is_manual_offer && (
                                      <span className="inline-block text-xs font-bold text-white bg-violet-600 px-2 py-0.5 rounded shadow-sm">
                                        MANUALE
                                      </span>
                                    )}
                                    {offer.vehicle_info?.use_4vantage && (
                                      <span className="inline-block text-xs font-bold text-white px-2 py-0.5 rounded shadow-sm" style={{ backgroundColor: '#7B1FA2' }} data-testid={`offer-4vantage-badge-${offer.id}`}>
                                        4Vantage
                                      </span>
                                    )}
                                  </div>
                                  <h3 className={`font-bold text-lg ${(isSold || isNonInteressatoCliente) ? 'text-slate-500' : 'text-slate-800'}`}>
                                    {offer.vehicle_info?.marca} {offer.vehicle_info?.modello}
                                  </h3>
                                  {offer.vehicle_info?.versione && (
                                    <p className="text-sm text-slate-500 truncate max-w-md">
                                      {offer.vehicle_info.versione}
                                    </p>
                                  )}
                                  {/* Info Usato inline (Targa e KM) - BEN VISIBILI */}
                                  {isUsato && (offer.vehicle_info?.targa || offer.vehicle_info?.km) && (
                                    <div className="flex flex-wrap gap-2 mt-1">
                                      {offer.vehicle_info?.targa && (
                                        <Badge className="bg-amber-100 text-amber-700 border border-amber-300 text-xs">
                                          Targa: {offer.vehicle_info.targa}
                                        </Badge>
                                      )}
                                      {offer.vehicle_info?.km && (
                                        <Badge className="bg-amber-100 text-amber-700 border border-amber-300 text-xs">
                                          {offer.vehicle_info.km.toLocaleString('it-IT')} km
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Canone - Prominente */}
                                <div className="text-center sm:text-right">
                                  <p className="text-2xl sm:text-3xl font-bold text-emerald-600">
                                    €{offer.canone_finale?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                  </p>
                                  <p className="text-xs text-slate-500">al mese IVA Escl.</p>
                                </div>
                              </div>
                              
                              {/* Info Rapide + Expand */}
                              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3.5 h-3.5" />
                                    {offer.durata_mesi} mesi
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Gauge className="w-3.5 h-3.5" />
                                    {formatKmAnnui(offer)}{isLongrunOffer(offer) ? '' : ' km/anno'}
                                  </span>
                                  {offer.anticipo > 0 && (
                                    <span className="flex items-center gap-1">
                                      <Euro className="w-3.5 h-3.5" />
                                      Anticipo €{offer.anticipo.toLocaleString('it-IT')}
                                    </span>
                                  )}
                                </div>
                                
                                {/* Toggle Expand */}
                                <div className="flex items-center gap-1 text-emerald-600 text-xs font-medium">
                                  <span className="hidden sm:inline">{isExpanded ? 'Nascondi dettagli' : 'Vedi dettagli'}</span>
                                  <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Sezione Espansa - Player + Dati Tecnici */}
                        {isExpanded && (
                          <div className="px-4 pb-4 pt-0 border-t border-slate-100 bg-slate-50/50">
                            
                            {/* PLAYER NEXTLANE - Elemento principale in alto */}
                            {hasPlayer && (
                              <div className="mt-4 mb-4">
                                <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                                  <Car className="w-3.5 h-3.5" />
                                  Foto Veicolo 360°
                                </h4>
                                <div className="relative w-full aspect-video bg-slate-900 rounded-lg overflow-hidden shadow-md">
                                  <iframe
                                    src={offer.vehicle_info.link_immagine}
                                    className="absolute inset-0 w-full h-full border-0"
                                    title={`Foto 360° ${offer.vehicle_info?.marca} ${offer.vehicle_info?.modello}`}
                                    allow="autoplay; fullscreen"
                                    allowFullScreen
                                  />
                                </div>
                                <p className="text-xs text-slate-400 mt-1 text-center">
                                  Clicca e trascina per ruotare la vista 360°
                                </p>
                              </div>
                            )}
                            
                            {/* Griglia Dati Finanziari */}
                            <div className="mt-4">
                              <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                                <Euro className="w-3.5 h-3.5" />
                                Dati Finanziari
                              </h4>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                <div className="p-3 bg-white rounded-lg border border-slate-200">
                                  <p className="text-[10px] text-slate-500 uppercase">Anticipo</p>
                                  <p className="text-sm font-semibold text-slate-800">€{(offer.anticipo || 0).toLocaleString('it-IT')}</p>
                                </div>
                                <div className="p-3 bg-white rounded-lg border border-slate-200">
                                  <p className="text-[10px] text-slate-500 uppercase">Durata</p>
                                  <p className="text-sm font-semibold text-slate-800">{offer.durata_mesi} mesi</p>
                                </div>
                                <div className="p-3 bg-white rounded-lg border border-slate-200">
                                  <p className="text-[10px] text-slate-500 uppercase">Km Annui</p>
                                  <p className="text-sm font-semibold text-slate-800">{formatKmAnnui(offer)}</p>
                                </div>
                                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                                  <p className="text-[10px] text-emerald-600 uppercase">Canone Mensile</p>
                                  <p className="text-sm font-bold text-emerald-700">€{offer.canone_finale?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                                </div>
                              </div>
                            </div>

                            {/* Fase 2 Task 2.2 — Configuratore canone cliente (doppio canone + slider) */}
                            {isCliente && offer.is_referente && !isSold && !isNonInteressatoCliente && (
                              <ReferenteProposalPanel offer={offer} onOfferChanged={fetchOffers} />
                            )}
                            {isCliente && !offer.is_referente && (offer.vehicle_id || offer.vehicle_info?.vehicle_id) && !isSold && !isNonInteressatoCliente && (
                              <>
                                <ClienteQuoteCalculator
                                  vehicleId={offer.vehicle_id || offer.vehicle_info?.vehicle_id}
                                  durata={offer.durata_mesi || preferenze?.durata || 48}
                                  km={offer.km_annui || preferenze?.km || 15000}
                                  initialAnticipoIvato={offer.anticipo_ivato || 0}
                                  offer={offer}
                                  onOfferChanged={fetchOffers}
                                />
                                {offer.saved_by_client && !offer.visto_verde && !offer.documenti_conferma_at && (
                                  <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (!window.confirm(`Vuoi rimuovere "${offer.vehicle_info?.marca || ''} ${offer.vehicle_info?.modello || ''}" dai tuoi preventivi? Libererai uno slot per aggiungere un\'altra auto dal catalogo.`)) return;
                                        try {
                                          await axios.delete(`${API}/client/preventivi/${offer.id}`, { headers: getAuthHeader() });
                                          toast.success('Preventivo rimosso. Ora hai uno slot libero.');
                                          await fetchOffers();
                                        } catch (err) {
                                          toast.error(err.response?.data?.detail || 'Errore nella rimozione');
                                        }
                                      }}
                                      data-testid={`btn-rimuovi-preventivo-${offer.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" /> Rimuovi dai preventivi
                                    </Button>
                                  </div>
                                )}
                              </>
                            )}
                            
                            {/* Griglia Dati Veicolo */}
                            {(offer.vehicle_info?.alimentazione || offer.vehicle_info?.cambio || offer.vehicle_info?.colore || (isUsato && (offer.vehicle_info?.targa || offer.vehicle_info?.km))) && (
                              <div className="mt-4">
                                <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                                  <Car className="w-3.5 h-3.5" />
                                  Dati Veicolo
                                </h4>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                  {offer.vehicle_info?.alimentazione && (
                                    <div className="p-3 bg-white rounded-lg border border-slate-200">
                                      <p className="text-[10px] text-slate-500 uppercase flex items-center gap-1">
                                        <Fuel className="w-3 h-3" /> Alimentazione
                                      </p>
                                      <p className="text-sm font-semibold text-slate-800 capitalize">{offer.vehicle_info.alimentazione}</p>
                                    </div>
                                  )}
                                  {offer.vehicle_info?.cambio && (
                                    <div className="p-3 bg-white rounded-lg border border-slate-200">
                                      <p className="text-[10px] text-slate-500 uppercase flex items-center gap-1">
                                        <Settings className="w-3 h-3" /> Cambio
                                      </p>
                                      <p className="text-sm font-semibold text-slate-800 capitalize">{offer.vehicle_info.cambio}</p>
                                    </div>
                                  )}
                                  {offer.vehicle_info?.colore && (
                                    <div className="p-3 bg-white rounded-lg border border-slate-200">
                                      <p className="text-[10px] text-slate-500 uppercase flex items-center gap-1">
                                        <Palette className="w-3 h-3" /> Colore
                                      </p>
                                      <p className="text-sm font-semibold text-slate-800 capitalize">{offer.vehicle_info.colore}</p>
                                    </div>
                                  )}
                                  {/* Campi specifici per veicoli USATI - BEN VISIBILI */}
                                  {isUsato && offer.vehicle_info?.targa && (
                                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                                      <p className="text-[10px] text-amber-600 uppercase font-medium">Targa</p>
                                      <p className="text-sm font-bold text-amber-800">{offer.vehicle_info.targa}</p>
                                    </div>
                                  )}
                                  {isUsato && offer.vehicle_info?.km && (
                                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                                      <p className="text-[10px] text-amber-600 uppercase font-medium">Km Attuali</p>
                                      <p className="text-sm font-bold text-amber-800">{offer.vehicle_info.km.toLocaleString('it-IT')} km</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Note (se presenti) */}
                            {offer.note && (
                              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <p className="text-xs text-blue-600 font-medium mb-1">Note</p>
                                <p className="text-sm text-blue-800">{offer.note}</p>
                              </div>
                            )}
                            
                            {/* Footer con Data e Azioni */}
                            <div className="mt-4 pt-4 border-t border-slate-200">
                              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                                <p className="text-xs text-slate-500">
                                  Preventivo del {new Date(offer.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}
                                  {offer.created_by && <span className="ml-1">• Consulente: {offer.created_by}</span>}
                                </p>
                                
                                {/* Offerta RIFIUTATA - Non Interessato */}
                                {isNonInteressatoCliente ? (
                                  <div className="w-full sm:w-auto text-center pointer-events-auto">
                                    <p className="text-sm text-slate-600 font-medium">
                                      Hai indicato di non essere interessato
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">
                                      Questa offerta verrà rimossa tra 2 giorni
                                    </p>
                                  </div>
                                ) : isSold ? (
                                  /* Veicolo VENDUTO */
                                  <div className="w-full sm:w-auto text-center">
                                    <p className="text-sm text-red-600 font-medium">
                                      Questa offerta non è più disponibile
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">
                                      Il veicolo è stato venduto
                                    </p>
                                  </div>
                                ) : isPendingDocs ? (
                                  /* Offerta ACCETTATA - In attesa documentazione */
                                  <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                                    <Button
                                      onClick={(e) => { e.stopPropagation(); handleDownloadPdf(offer.id, false); }}
                                      variant="outline"
                                      className="w-full sm:w-auto"
                                      data-testid={`download-pdf-${offer.id}`}
                                    >
                                      <Download className="w-4 h-4 mr-2" />
                                      Scarica PDF
                                    </Button>
                                    {!offer.visto_verde && !offer.documenti_conferma_at && (
                                    <Button
                                      onClick={(e) => { e.stopPropagation(); handleCancelAccept(offer.id); }}
                                      variant="ghost"
                                      className="w-full sm:w-auto text-slate-500 hover:text-red-600"
                                      data-testid={`cancel-accept-${offer.id}`}
                                    >
                                      <Undo2 className="w-4 h-4 mr-2" />
                                      Annulla Accettazione
                                    </Button>
                                    )}
                                  </div>
                                ) : (
                                  /* Offerta NORMALE - Pulsanti Download + Accetta + Non Interessato */
                                  <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                                    <Button
                                      onClick={(e) => { e.stopPropagation(); handleDownloadPdf(offer.id, false); }}
                                      variant="outline"
                                      className="w-full sm:w-auto"
                                      data-testid={`download-pdf-${offer.id}`}
                                    >
                                      <Download className="w-4 h-4 mr-2" />
                                      Scarica PDF
                                    </Button>
                                    {/* Legacy "Accetta Offerta" nascosto: il cliente usa il flusso Visto Verde nel calcolatore sottostante */}
                                    <Button
                                      onClick={(e) => { e.stopPropagation(); handleNotInterested(offer.id); }}
                                      variant="outline"
                                      className="w-full sm:w-auto border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                                      data-testid={`not-interested-${offer.id}`}
                                    >
                                      <XCircle className="w-4 h-4 mr-2" />
                                      Non Interessato
                                    </Button>
                                  </div>
                                )}
                              </div>
                              
                              {/* Info aggiuntiva per offerte accettate (legacy - solo se non c'è ancora un flusso Visto Verde) */}
                              {isPendingDocs && !isSold && !isNonInteressatoCliente && !offer.visto_verde && !offer.documenti_conferma_at && (
                                <div className="mt-3 p-3 bg-violet-50 rounded-lg border border-violet-200">
                                  <p className="text-sm text-violet-700 flex items-center gap-2">
                                    <Mail className="w-4 h-4" />
                                    <span>
                                      Invia la documentazione richiesta a: <strong>{offer.agent_email}</strong>
                                    </span>
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nessun preventivo disponibile</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Sfoglia il catalogo e salva fino a 3 auto da confrontare.
                  </p>
                  {isCliente && (
                    <Button
                      className="mt-4 gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => navigate('/dashboard/catalogo-offerte')}
                      data-testid="btn-empty-goto-catalogo"
                    >
                      <Plus className="w-4 h-4" /> Vai al Catalogo Offerte
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          ))}

          {/* Offerte Archiviate del Cliente */}
          {archivedOffers.length > 0 && (
            <Card>
              <CardHeader 
                className="cursor-pointer"
                onClick={() => setShowArchived(!showArchived)}
              >
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-slate-400" />
                    <span className="text-slate-600">Archivio ({archivedOffers.length})</span>
                  </div>
                  <Badge variant="outline" className="text-slate-500">
                    {showArchived ? 'Nascondi' : 'Mostra'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              {showArchived && (
                <CardContent>
                  <p className="text-sm text-slate-500 mb-4">
                    Preventivi più vecchi di 6 mesi. Puoi comunque scaricare i PDF.
                  </p>
                  <div className="space-y-3">
                    {archivedOffers.map(offer => (
                      <div 
                        key={offer.id}
                        className="p-3 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          {offer.vehicle_info?.immagine ? (
                            <img 
                              src={offer.vehicle_info.immagine} 
                              alt={offer.vehicle_info?.modello}
                              className="w-12 h-9 object-cover rounded"
                            />
                          ) : (
                            <div className="w-12 h-9 bg-slate-200 rounded flex items-center justify-center">
                              <Car className="w-5 h-5 text-slate-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-slate-700">
                              {offer.vehicle_info?.marca} {offer.vehicle_info?.modello}
                            </p>
                            <p className="text-xs text-slate-400">
                              {new Date(offer.created_at).toLocaleDateString('it-IT')} • €{offer.canone_finale?.toLocaleString('it-IT')}/mese
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadPdf(offer.id, false)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Fase 2 Sub-release 2.4b — Sezione Usati Velati (max 3, click → richiedi sblocco) */}
          <UsatiVelatiSection
            canoneRiferimento={
              (offers && offers[0] && (offers[0].canone_finale || offers[0].canone_base)) || 0
            }
          />
        </div>
      ) : (
        /* Vista Admin/Agente - Card Espandibili */
        <>
      {/* Barra di Ricerca - Solo Admin/Agente */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Cerca per codice (es. MN-2026-0001), cliente o targa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                fetchOffers(searchTerm);
              }
            }}
            className="pl-9 pr-4"
            data-testid="offers-search-input"
          />
        </div>
        <Button 
          onClick={() => fetchOffers(searchTerm)}
          variant="outline"
          data-testid="offers-search-btn"
        >
          <Search className="w-4 h-4 mr-2" />
          Cerca
        </Button>
        {searchTerm && (
          <Button 
            onClick={() => {
              setSearchTerm('');
              fetchOffers('');
            }}
            variant="ghost"
            size="sm"
          >
            <X className="w-4 h-4 mr-1" />
            Reset
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Elenco Offerte ({offers.length})
            {searchTerm && <span className="text-sm font-normal text-slate-500">- Filtro: "{searchTerm}"</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-24 bg-slate-100 rounded animate-pulse" />
              ))}
            </div>
          ) : offers.length > 0 ? (
            <div className="space-y-3">
              {offers.map(offer => {
                const isApprovato = offer.esito === 'approvato';
                const isContrattoFirmato = offer.esito === 'contratto_firmato';
                const isAccettatoCliente = isOffertaAccettata(offer);
                const isPrioritaEsito = isApprovato || isContrattoFirmato;
                const isExpanded = expandedOffers[offer.id];
                const tipoVeicolo = offer.vehicle_info?.tipo || 'nuovo';
                
                // STATO "VENDUTO" - Veicolo non più disponibile
                // Considera sia is_sold (offerta su veicolo venduto direttamente)
                // sia is_sold_vehicle (vehicle_id rimosso dal listino - copre anche le offerte clonate)
                const isSold = offer.is_sold === true || offer.is_sold_vehicle === true;
                
                // STATO "NON INTERESSATO" - Rifiutato dal cliente
                const isNonInteressato = offer.esito === 'non_interessato' || offer.status === 'rifiutato_cliente';
                
                // STATO "RIFIUTATA" - Pratica rifiutata dall'Admin
                const isRifiutataAdmin = offer.esito === 'rifiutato';
                
                // STATO "PERSO" - Offerta persa per concorrenza o prezzo
                const isPerso = offer.esito === 'perso_concorrenza' || offer.esito === 'perso_prezzo';
                
                // STATO "OFFERTA NON VALIDA" - Marcata come invalida da Admin/Top Agent
                const isNonValida = offer.esito === 'offerta_non_valida' || offer.is_non_valida;
                
                // Offerta da nascondere/disabilitare (rifiutata, non interessato, persa, o venduto)
                const isRifiutata = isNonInteressato || isRifiutataAdmin || isPerso || isNonValida || isSold;
                
                return (
                  <div 
                    key={offer.id}
                    className={`rounded-lg border-2 transition-all relative ${
                      isSold
                        ? 'border-red-500 bg-red-50 opacity-80'  // Bordo rosso per venduto
                        : isNonValida
                        ? 'border-red-600 bg-red-100 opacity-85'  // Bordo rosso intenso per non valide
                        : isRifiutataAdmin
                        ? 'border-red-800 bg-red-50 opacity-80'  // Bordo rosso per rifiutate Admin
                        : isPerso
                          ? 'border-orange-600 bg-orange-50 opacity-80'  // Bordo arancione per perse
                          : isNonInteressato
                            ? 'border-slate-800 bg-slate-100 opacity-75'  // Sfondo grigio/nero per non interessato
                            : isAccettatoCliente
                              ? 'border-violet-400 bg-violet-50/50 ring-2 ring-violet-200'
                              : isPrioritaEsito 
                                ? 'border-emerald-400 bg-emerald-50/50 ring-2 ring-emerald-200' 
                                : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                    data-testid={`offer-card-${offer.id}`}
                  >
                    {/* TIMBRO ROSSO "VENDUTO" - Veicolo non più disponibile */}
                    {isSold && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                        <div className="bg-red-600/95 text-white px-6 py-3 rounded-lg shadow-2xl transform -rotate-6 border-4 border-red-400">
                          <div className="text-xl font-black tracking-wider text-center uppercase">
                            VENDUTO
                          </div>
                          <div className="text-xs text-red-200 text-center mt-1">
                            Veicolo non più disponibile
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* TIMBRO ROSSO "OFFERTA NON VALIDA" - Eliminazione automatica dopo 48h */}
                    {isNonValida && !isSold && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                        <div className="bg-red-700/95 text-white px-6 py-3 rounded-lg shadow-2xl transform -rotate-6 border-4 border-red-500">
                          <div className="text-xl font-black tracking-wider text-center uppercase">
                            ⛔ NON VALIDA
                          </div>
                          <div className="text-xs text-red-200 text-center mt-1">
                            Eliminazione automatica in 48h
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* TIMBRO ROSSO "RIFIUTATA" - Pratica rifiutata dall'Admin */}
                    {isRifiutataAdmin && !isNonValida && !isSold && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                        <div className="bg-red-900/95 text-white px-6 py-3 rounded-lg shadow-2xl transform -rotate-6 border-4 border-red-700">
                          <div className="text-xl font-black tracking-wider text-center uppercase">
                            RIFIUTATA
                          </div>
                          <div className="text-xs text-red-200 text-center mt-1">
                            Pratica non approvata
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* TIMBRO ARANCIONE "PERSO" - Offerta persa per concorrenza/prezzo */}
                    {isPerso && !isRifiutataAdmin && !isNonValida && !isSold && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                        <div className="bg-orange-800/95 text-white px-6 py-3 rounded-lg shadow-2xl transform -rotate-6 border-4 border-orange-600">
                          <div className="text-xl font-black tracking-wider text-center uppercase">
                            PERSO
                          </div>
                          <div className="text-xs text-orange-200 text-center mt-1">
                            {offer.esito === 'perso_concorrenza' ? 'Concorrenza' : 'Prezzo'}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* TIMBRO NERO "NON INTERESSATO" */}
                    {isNonInteressato && !isRifiutataAdmin && !isNonValida && !isSold && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                        <div className="bg-slate-900/95 text-white px-6 py-3 rounded-lg shadow-2xl transform -rotate-6 border-4 border-slate-700">
                          <div className="text-xl font-black tracking-wider text-center uppercase">
                            Non Interessato
                          </div>
                          <div className="text-xs text-slate-300 text-center mt-1">
                            Offerta Rifiutata dal Cliente
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Header VENDUTO */}
                    {isSold && (
                      <div className="bg-red-600 text-white px-3 py-2 text-center">
                        <p className="text-sm font-bold uppercase tracking-wide">
                          🚗 Veicolo Venduto - Offerta Non Disponibile
                        </p>
                      </div>
                    )}
                    
                    {/* Header RIFIUTATA - Pratica rifiutata dall'Admin */}
                    {isRifiutataAdmin && !isSold && (
                      <div className="bg-red-800 text-white px-3 py-2 text-center">
                        <p className="text-sm font-bold uppercase tracking-wide">
                          ❌ Pratica Rifiutata - Verrà archiviata tra 48h
                        </p>
                      </div>
                    )}
                    
                    {/* Header PERSO - Offerta persa */}
                    {isPerso && !isRifiutataAdmin && !isSold && (
                      <div className="bg-orange-700 text-white px-3 py-2 text-center">
                        <p className="text-sm font-bold uppercase tracking-wide">
                          📉 Offerta Persa - {offer.esito === 'perso_concorrenza' ? 'Concorrenza' : 'Prezzo'}
                        </p>
                      </div>
                    )}
                    
                    {/* Header NON INTERESSATO */}
                    {isNonInteressato && !isRifiutataAdmin && !isSold && (
                      <div className="bg-slate-800 text-white px-3 py-2 text-center">
                        <p className="text-sm font-bold uppercase tracking-wide">
                          ⛔ Offerta Rifiutata - Non Interessato
                        </p>
                      </div>
                    )}
                    
                    {/* Header Card - Sempre visibile */}
                    <div 
                      className={`p-3 ${(isRifiutata && !isAdmin) ? 'cursor-default opacity-60' : 'cursor-pointer'}`}
                      onClick={() => (isAdmin || !isRifiutata) && toggleExpand(offer.id)}
                    >
                      <div className="flex items-center gap-3">
                        {/* Immagine/Anteprima Veicolo */}
                        <div className="relative flex-shrink-0">
                          <VehicleThumbnail marca={offer.vehicle_info?.marca} modello={offer.vehicle_info?.modello} linkImmagine={offer.vehicle_info?.link_immagine || offer.link_immagine} grayscale={isRifiutata} isUsato={tipoVeicolo === 'usato'} />
                          {/* Badge Tipo */}
                          <div className="absolute -top-1 -left-1">
                            <TipoBadge tipo={tipoVeicolo} />
                          </div>
                          {/* Badge Priorità - Accettato Cliente (viola) o Esito (verde) */}
                          {isAccettatoCliente && !isRifiutata && (
                            <div className="absolute -bottom-1 -right-1">
                              <Badge className="bg-violet-500 text-white text-[9px] px-1">
                                ★
                              </Badge>
                            </div>
                          )}
                          {isPrioritaEsito && !isAccettatoCliente && !isRifiutata && (
                            <div className="absolute -bottom-1 -right-1">
                              <Badge className="bg-emerald-500 text-white text-[9px] px-1">
                                {isContrattoFirmato ? '✓' : '!'}
                              </Badge>
                            </div>
                          )}
                        </div>
                        
                        {/* Info Veicolo + Cliente */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              {/* Codice Offerta + VehicleID - BEN VISIBILI */}
                              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                {offer.codice_offerta && (
                                  <span className="inline-block text-[10px] font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                    {offer.codice_offerta}
                                  </span>
                                )}
                                {offer.promo_id_label ? (
                                  <span className="inline-block text-[10px] font-mono text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                                    {offer.promo_id_label}
                                  </span>
                                ) : (offer.vehicle_info?.vehicle_id || offer.vehicle_id) ? (
                                  <span className="inline-block text-[10px] font-mono text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                                    ID: {offer.vehicle_info?.vehicle_id || offer.vehicle_id}
                                  </span>
                                ) : null}
                                {offer.vehicle_info?.use_4vantage && (
                                  <span className="inline-block text-[10px] font-bold text-white px-1.5 py-0.5 rounded" style={{ backgroundColor: '#7B1FA2' }} data-testid={`offer-4vantage-badge-${offer.id}`}>
                                    4Vantage
                                  </span>
                                )}
                              </div>
                              <p className="font-semibold text-slate-800 text-sm">
                                {offer.vehicle_info?.marca} {offer.vehicle_info?.modello}
                              </p>
                              <p className="text-xs text-slate-500">
                                {offer.durata_mesi} mesi • {formatKmAnnui(offer)}{isLongrunOffer(offer) ? '' : ' km/anno'}
                                {offer.anticipo > 0 && ` • Ant. €${offer.anticipo.toLocaleString('it-IT')}`}
                              </p>
                              {/* Info Usato (Targa, Km) */}
                              {tipoVeicolo === 'usato' && (
                                <p className="text-[10px] text-amber-600 mt-0.5">
                                  {offer.vehicle_info?.targa && `Targa: ${offer.vehicle_info.targa}`}
                                  {offer.vehicle_info?.km && ` • ${offer.vehicle_info.km.toLocaleString('it-IT')} km`}
                                </p>
                              )}
                            </div>
                            {/* Canone */}
                            <div className="text-right">
                              <p className="font-bold text-lg text-emerald-600">
                                €{offer.canone_finale?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                              </p>
                              <p className="text-[10px] text-slate-500">/mese IVA escl.</p>
                            </div>
                          </div>
                          
                          {/* Riga Info Cliente + Stato */}
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                            <div className="flex items-center gap-2">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                offer.contact_info?.tipo_profilo === 'prospect' ? 'bg-violet-100' : 'bg-blue-100'
                              }`}>
                                <User className={`w-3 h-3 ${
                                  offer.contact_info?.tipo_profilo === 'prospect' ? 'text-violet-600' : 'text-blue-600'
                                }`} />
                              </div>
                              <div>
                                <p className="text-xs font-medium text-slate-700">
                                  {offer.contact_info?.nome_cognome || 'N/A'}
                                </p>
                                {/* Mostra agente se diverso dall'utente corrente (per Admin e Capogruppo) */}
                                {offer.user_name && offer.user_id !== user?.id && (
                                  <p className="text-[10px] text-slate-400">
                                    Agente: {offer.user_name}
                                    {offer.created_by_admin && (
                                      <span className="ml-1 text-amber-600">(creata da Admin)</span>
                                    )}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            {/* Badge Accettato + Esito + Stato Invio */}
                            <div className="flex items-center gap-2">
                              {/* Badge Creata da Admin (per agenti) */}
                              {!isAdmin && offer.created_by_admin && (
                                <Badge className="bg-amber-100 text-amber-700 border border-amber-300 text-[10px] px-1.5">
                                  📋 da Admin
                                </Badge>
                              )}
                              {/* Badge Accettato dal Cliente */}
                              {isAccettatoCliente && (
                                <Badge className="bg-violet-100 text-violet-700 border border-violet-300 text-[10px] px-1.5">
                                  ★ Accettato
                                </Badge>
                              )}
                              <button 
                                onClick={(e) => { e.stopPropagation(); handleOpenEsitoDialog(offer); }} 
                                className="hover:opacity-80"
                              >
                                {getEsitoBadge(offer.esito)}
                              </button>
                              <StatoInvio inviata={offer.inviata_cliente} inviatAt={offer.inviata_cliente_at} />
                              {/* Menu Admin - Sempre visibile nell'header */}
                              {isAdmin && (
                                <div onClick={(e) => e.stopPropagation()}>
                                  <AdminOfferControls offer={offer} onUpdate={fetchOffers} />
                                </div>
                              )}
                              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Dettagli Espansi */}
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-0 border-t border-slate-100">
                        {/* Info Accettazione Cliente (se accettato) */}
                        {isAccettatoCliente && (
                          <div className="mt-3 p-2 bg-violet-50 rounded-lg border border-violet-200">
                            <p className="text-xs text-violet-700 flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4" />
                              <span>
                                <strong>Cliente ha accettato l&apos;offerta</strong>
                                {offer.accepted_at && (
                                  <span className="ml-2 text-violet-500">
                                    il {new Date(offer.accepted_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                              </span>
                            </p>
                            <p className="text-[10px] text-violet-500 mt-1">In attesa di documentazione dal cliente. Procedi con la gestione dell&apos;esito.</p>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                          {/* Dettagli Tecnici */}
                          <div className="p-2 bg-slate-50 rounded-lg">
                            <p className="text-[10px] text-slate-500 uppercase">Durata</p>
                            <p className="text-sm font-medium text-slate-800">{offer.durata_mesi} mesi</p>
                          </div>
                          <div className="p-2 bg-slate-50 rounded-lg">
                            <p className="text-[10px] text-slate-500 uppercase">Km/Anno</p>
                            <p className="text-sm font-medium text-slate-800">{formatKmAnnui(offer)}</p>
                          </div>
                          <div className="p-2 bg-slate-50 rounded-lg">
                            <p className="text-[10px] text-slate-500 uppercase">Anticipo</p>
                            <p className="text-sm font-medium text-slate-800">€{offer.anticipo?.toLocaleString('it-IT') || 0}</p>
                          </div>
                          <div className="p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                            <p className="text-[10px] text-emerald-600 uppercase">Canone</p>
                            <p className="text-sm font-bold text-emerald-700">€{offer.canone_finale?.toLocaleString('it-IT') || 0}</p>
                          </div>
                        </div>
                        
                        {/* Riepilogo Servizi Opzionali (Franchigie / Gomme / Auto Sostitutiva) */}
                        {(() => {
                          const franchigiaMap = {
                            '1000-10': '€1.000 / 10%',
                            '1000-0': '€1.000 / 0%',
                            '500-10': '€500 / 10%',
                            '500-0': '€500 / 0%'
                          };
                          const mf = offer.manual_franchigie;
                          let franchigiaLabel = null;
                          if (mf && (mf.rca || mf.kasko || mf.if)) {
                            franchigiaLabel = `RCA ${mf.rca || '-'} • Kasko ${mf.kasko || '-'} • I&F ${mf.if || '-'}`;
                          } else if (offer.franchigia_scelta) {
                            franchigiaLabel = franchigiaMap[offer.franchigia_scelta] || offer.franchigia_scelta;
                          }
                          const gommeInv = offer.gomme_invernali || 0;
                          const gommeEst = offer.gomme_estive || 0;
                          const gommeParts = [];
                          if (gommeInv > 0) gommeParts.push(`${gommeInv} Inv`);
                          if (gommeEst > 0) gommeParts.push(`${gommeEst} Est`);
                          const gommeText = offer.manual_gomme_testo || (gommeParts.length ? gommeParts.join(' + ') : null);
                          const autoSost = offer.auto_sostitutiva_categoria;
                          const costoAutoSost = offer.costo_auto_sostitutiva || 0;
                          const costoGomme = offer.costo_gomme || 0;
                          const extraFranchigia = offer.extra_franchigia || 0;
                          const hasAnyService = franchigiaLabel || gommeText || autoSost;
                          if (!hasAnyService) return null;
                          return (
                            <div className="mt-3 p-3 bg-blue-50/60 rounded-lg border border-blue-100">
                              <p className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide mb-2">Servizi inclusi</p>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                {franchigiaLabel && (
                                  <div className="flex flex-col gap-0.5 px-2 py-1.5 bg-white rounded border border-blue-100">
                                    <span className="text-[9px] uppercase text-slate-500">Franchigie</span>
                                    <span className="text-xs font-medium text-slate-800 leading-snug">{franchigiaLabel}</span>
                                    {extraFranchigia > 0 && <span className="text-[10px] text-slate-500">+€{extraFranchigia.toFixed(2)}/mese</span>}
                                  </div>
                                )}
                                {gommeText && (
                                  <div className="flex flex-col gap-0.5 px-2 py-1.5 bg-white rounded border border-blue-100">
                                    <span className="text-[9px] uppercase text-slate-500">Gomme</span>
                                    <span className="text-xs font-medium text-slate-800 leading-snug">{gommeText}</span>
                                    {costoGomme > 0 && <span className="text-[10px] text-slate-500">+€{costoGomme.toFixed(2)}/mese</span>}
                                  </div>
                                )}
                                {autoSost && (
                                  <div className="flex flex-col gap-0.5 px-2 py-1.5 bg-white rounded border border-blue-100">
                                    <span className="text-[9px] uppercase text-slate-500">Auto Sostitutiva</span>
                                    <span className="text-xs font-medium text-slate-800 leading-snug">Categoria {autoSost}</span>
                                    {costoAutoSost > 0 && <span className="text-[10px] text-slate-500">+€{costoAutoSost.toFixed(2)}/mese</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                        
                        {/* Sezione Provvigioni - Visibile solo per Admin/Agente (compatta, in coda) */}
                        {(isAdmin || isAgente) && (() => {
                          // Calcola provvigioni corrette per impersonificazione
                          const commDisplay = calculateDisplayCommission(offer);
                          return (
                          <div className={`mt-3 px-3 py-2 rounded-md flex items-center justify-between gap-3 ${
                            commDisplay.is_lead_vetrina
                              ? 'bg-orange-50 border border-orange-200'
                              : 'bg-emerald-50 border border-emerald-200'
                          }`}>
                            <span className={`text-[10px] uppercase tracking-wide font-medium ${
                              commDisplay.is_lead_vetrina ? 'text-orange-600' : 'text-emerald-700'
                            }`}>
                              Tue Provvigioni {impersonatedUser && `(${impersonatedUser.competenza_provvigioni || 30}%)`}
                            </span>
                            {commDisplay.is_lead_vetrina ? (
                              <span className="flex items-center gap-2 text-xs">
                                <span className="text-slate-400 line-through">€{commDisplay.provvigione_pre_sconto.toLocaleString('it-IT')}</span>
                                <span className="text-orange-600">-{commDisplay.sconto_vetrina_pct}%</span>
                                <span className="text-base font-bold text-orange-600">€{commDisplay.provvigione_agente.toLocaleString('it-IT')}</span>
                                <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-[9px] px-1.5">Vetrina</Badge>
                              </span>
                            ) : (
                              <span className="text-base font-bold text-emerald-600">€{commDisplay.provvigione_agente.toLocaleString('it-IT')}</span>
                            )}
                          </div>
                          );
                        })()}
                        
                        {/* Info Veicolo Aggiuntive */}
                        {(offer.vehicle_info?.versione || offer.vehicle_info?.alimentazione) && (
                          <div className="mt-3 p-2 bg-blue-50 rounded-lg">
                            <p className="text-xs text-blue-700">
                              <span className="font-medium">Versione:</span> {offer.vehicle_info?.versione || 'N/D'}
                              {offer.vehicle_info?.alimentazione && (
                                <span className="ml-3"><span className="font-medium">Alimentazione:</span> {offer.vehicle_info.alimentazione}</span>
                              )}
                            </p>
                          </div>
                        )}
                        
                        {/* Contatto Cliente */}
                        <div className="mt-3 p-2 bg-violet-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-violet-700">
                                <span className="font-medium">Tel:</span> {offer.contact_info?.telefono || 'N/D'}
                                <span className="ml-3"><span className="font-medium">Email:</span> {offer.contact_info?.email || 'N/D'}</span>
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              {(offer.is_lead_vetrina || offer.contact_info?.origine === 'vetrina_pubblica') && (
                                <Badge className="bg-orange-100 text-orange-700 border border-orange-300 text-[10px]">
                                  🏪 Vetrina
                                </Badge>
                              )}
                              <Badge className={`text-[10px] ${
                                offer.contact_info?.tipo_profilo === 'prospect' 
                                  ? 'bg-violet-100 text-violet-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {offer.contact_info?.tipo_profilo || 'lead'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        {/* Scadenza e Date */}
                        <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                          <div className="flex items-center gap-4">
                            <span>Creata: {new Date(offer.created_at).toLocaleDateString('it-IT')}</span>
                            {offer.scadenza && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Scade: {new Date(offer.scadenza).toLocaleDateString('it-IT')}
                              </span>
                            )}
                          </div>
                          {getScadenzaBadge(offer)}
                        </div>
                        
                        {/* Azioni */}
                        <div className="mt-3 flex flex-wrap items-center justify-end gap-2 pt-3 border-t border-slate-100">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleDownloadPdf(offer.id); }}
                            data-testid={`download-pdf-${offer.id}`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Stampa PDF
                          </Button>
                          
                          {offer.contact_info?.tipo_profilo && offer.contact_info?.censimento_ayvens_status !== 'approvato' && (
                            <AyvensStatusBadge status={offer.contact_info?.censimento_ayvens_status} testId={`offer-ayvens-badge-${offer.id}`} />
                          )}
                          {/* Pulsante Invia manuale - sempre visibile se c'è un contatto */}
                          {offer.contact_info?.tipo_profilo && (
                            <Button 
                              variant="outline"
                              size="sm"
                              className="border-blue-300 text-blue-600 hover:bg-blue-50"
                              onClick={(e) => { e.stopPropagation(); handleSendToClient(offer.id); }}
                              disabled={offer.contact_info?.censimento_ayvens_status !== 'approvato'}
                              title={offer.contact_info?.censimento_ayvens_status !== 'approvato' ? 'Invio bloccato: censimento Ayvens non approvato' : undefined}
                              data-testid={`send-to-client-${offer.id}`}
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Invia Email
                            </Button>
                          )}
                          
                          {/* Sollecito Admin */}
                          {isAdmin && 
                           offer.user_id !== user?.id && 
                           !isInClosingPhase(offer) && 
                           !offer.sollecito_admin && (
                            <Button 
                              variant="outline"
                              size="sm"
                              className="border-orange-300 text-orange-600 hover:bg-orange-50"
                              onClick={(e) => { e.stopPropagation(); handleSendSollecito(offer.id); }}
                            >
                              <AlertTriangle className="w-4 h-4 mr-1" />
                              Sollecita
                            </Button>
                          )}
                          
                          {/* Pulsante ORDINE - Solo per Contratto Firmato da cliente Prospect */}
                          {isAdmin && isContrattoFirmato && offer.contact_info?.tipo_profilo === 'prospect' && (
                            <Button 
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 ring-2 ring-emerald-300"
                              onClick={(e) => { e.stopPropagation(); handleOpenValidateDialog(offer); }}
                              data-testid={`ordine-btn-${offer.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              ORDINE
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-800">Nessuna offerta</h3>
              <p className="text-slate-500 mt-1">Le offerte generate appariranno qui</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Agent Offers Section (Admin only) */}
      {isAdmin && (
        <AgentOffersSection 
          agentOffers={agentOffers}
          showAgentOffers={showAgentOffers}
          setShowAgentOffers={setShowAgentOffers}
          onSendSollecito={handleSendSollecito}
        />
      )}
      </>
      )}

      {/* Dialogs - Solo per Admin/Agente */}
      {!isCliente && (
        <>
          <OfferOrdineDialog
            open={validateDialog.open}
            offer={validateDialog.offer}
            onClose={(open) => setValidateDialog({ open, offer: validateDialog.offer })}
            agents={agents}
            selectedAgentId={selectedAgentId}
            setSelectedAgentId={setSelectedAgentId}
            meseCompetenza={meseCompetenza}
            setMeseCompetenza={setMeseCompetenza}
            dataFineNoleggio={dataFineNoleggio}
            setDataFineNoleggio={setDataFineNoleggio}
            onConfirm={handleValidate}
          />

          <OfferEsitoDialog
            open={esitoDialog.open}
            offer={esitoDialog.offer}
            onClose={(open) => setEsitoDialog({ open, offer: esitoDialog.offer })}
            selectedEsito={selectedEsito}
            setSelectedEsito={setSelectedEsito}
            dataRichiamo={dataRichiamo}
            setDataRichiamo={setDataRichiamo}
            onSave={handleSaveEsito}
            userRole={user?.role}
            currentUserId={user?.id}
          />
        </>
      )}
      
      {/* Dialog Conferma Accettazione Offerta (Cliente) */}
      <Dialog open={acceptDialog.open} onOpenChange={(open) => !open && setAcceptDialog({ ...acceptDialog, open: false })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-700">
              <CheckCircle2 className="w-5 h-5" />
              Conferma Accettazione
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {acceptDialog.offer && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="font-medium text-slate-800">
                  {acceptDialog.offer.vehicle_info?.marca} {acceptDialog.offer.vehicle_info?.modello}
                </p>
                <p className="text-sm text-slate-500">
                  €{acceptDialog.offer.canone_finale?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}/mese • {acceptDialog.offer.durata_mesi} mesi
                </p>
              </div>
            )}
            
            <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
              <p className="text-sm text-violet-800 leading-relaxed">
                <strong>Grazie per la scelta!</strong>
                <br /><br />
                Per procedere con la pratica, invia la documentazione richiesta a:
              </p>
              <div className="mt-3 p-2 bg-white rounded border border-violet-200 flex items-center gap-2">
                <Mail className="w-4 h-4 text-violet-600" />
                <a 
                  href={`mailto:${acceptDialog.agentEmail}`}
                  className="text-violet-700 font-semibold hover:underline"
                >
                  {acceptDialog.agentEmail}
                </a>
              </div>
              <p className="text-xs text-violet-600 mt-3">
                Verrai ricontattato dal tuo consulente <strong>{acceptDialog.agentName}</strong> entro breve per definire la pratica.
              </p>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setAcceptDialog({ ...acceptDialog, open: false })}
              className="w-full sm:w-auto"
            >
              Annulla
            </Button>
            <Button
              onClick={handleAcceptOffer}
              disabled={acceptingOffer}
              className="w-full sm:w-auto bg-violet-600 hover:bg-violet-700"
            >
              {acceptingOffer ? 'Invio...' : 'Conferma Accettazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fase 2 Sub-release 2.4b — Modale preferenze durata/km al primo accesso cliente */}
      {isCliente && (
        <PreferenzeQuoteDialog
          open={showPreferenze}
          onClose={() => setShowPreferenze(false)}
          onSaved={(prefs) => setPreferenze(prefs)}
          initialDurata={preferenze?.durata || 48}
          initialKm={preferenze?.km || 15000}
        />
      )}
    </div>
  );
};

export default OffersPage;
